import { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toLocalDate } from '@/utils/dateHelpers';
import { cn } from '@/lib/utils';
import {
  Search,
  ArrowDownCircle,
  ArrowLeftRight,
  ArrowUpCircle,
  CheckCircle2,
  AlertCircle,
  Ban,
  Building2,
  Landmark,
  Filter,
  MoreHorizontal,
  Undo2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { usePermissions } from '@/hooks/usePermissions';
import { useReverseTransfer } from '@/hooks/useReverseTransfer';
import { useAccountTransactions, useAccountTransactionsSummary, type AccountTransaction } from '@/hooks/useAccountTransactions';

interface AccountMovementsPanelProps {
  accountId: string;
  startDate: string;
  endDate: string;
  requiresReconciliation?: boolean;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

const getReconciliationStatus = (transaction: any) => {
  // 'system' (pagamento de título) e 'ledger' (lançamento direto, R-FIN.1)
  // não são linhas bancárias — só 'bank' tem status de conciliação.
  if (transaction.source !== 'bank') {
    return { status: 'system', label: 'Sistema', color: 'bg-blue-100 text-blue-800' };
  }
  if (transaction.reconciliation_status === 'reconciled') {
    // A4 (D-140C-ii.5): reconciled_by NULL = vínculo do motor/pipeline
    return {
      status: 'reconciled',
      label: transaction.reconciled_by ? 'Conciliado' : 'Conciliado (auto)',
      color: 'bg-green-100 text-green-800',
    };
  }
  if (transaction.reconciliation_status === 'ignored') {
    return { status: 'ignored', label: 'Ignorado', color: 'bg-gray-100 text-gray-800' };
  }
  return { status: 'pending', label: 'Sem Conciliação', color: 'bg-yellow-100 text-yellow-800' };
};

const getReconciliationIcon = (status: string) => {
  switch (status) {
    case 'reconciled':
      return <CheckCircle2 className="h-3.5 w-3.5" />;
    case 'ignored':
      return <Ban className="h-3.5 w-3.5" />;
    case 'system':
      return <Building2 className="h-3.5 w-3.5" />;
    default:
      return <AlertCircle className="h-3.5 w-3.5" />;
  }
};

export const AccountMovementsPanel = ({
  accountId,
  startDate,
  endDate,
  requiresReconciliation = true,
}: AccountMovementsPanelProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'income' | 'expense'>('all');
  const [sourceFilter, setSourceFilter] = useState<'all' | 'system' | 'bank'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'reconciled' | 'pending' | 'ignored'>('all');
  const [reverseTarget, setReverseTarget] = useState<AccountTransaction | null>(null);

  const { canAccess, isMaster } = usePermissions();
  const canReverse = canAccess('finance:manage_transfers') || isMaster;
  const reverseTransfer = useReverseTransfer();

  const { data: transactions = [], isLoading } = useAccountTransactions({
    accountId,
    startDate,
    endDate,
    type: typeFilter,
    source: requiresReconciliation ? sourceFilter : 'all',
  });

  const summary = useAccountTransactionsSummary(transactions);

  const filteredTransactions = transactions.filter(tx => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch =
        tx.description.toLowerCase().includes(search) ||
        tx.customer_or_supplier_name?.toLowerCase().includes(search) ||
        tx.payment_value.toString().includes(search);
      if (!matchesSearch) return false;
    }

    if (requiresReconciliation && statusFilter !== 'all') {
      const { status } = getReconciliationStatus(tx);
      if (statusFilter === 'reconciled' && status !== 'reconciled') return false;
      if (statusFilter === 'pending' && status !== 'pending') return false;
      if (statusFilter === 'ignored' && status !== 'ignored') return false;
    }

    return true;
  });

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <Filter className="h-4 w-4" />
            Total de Movimentações
          </div>
          <p className="text-2xl font-bold">{summary.transactionCount}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <ArrowDownCircle className="h-4 w-4 text-green-600" />
            Entradas
          </div>
          <p className="text-xl font-bold text-green-600">{formatCurrency(summary.totalIncome)}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <ArrowUpCircle className="h-4 w-4 text-red-600" />
            Saídas
          </div>
          <p className="text-xl font-bold text-red-600">{formatCurrency(summary.totalExpense)}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            Saldo no Período
          </div>
          <p className={`text-xl font-bold ${summary.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrency(summary.balance)}
          </p>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Pesquisar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as any)}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="income">Entradas</SelectItem>
            <SelectItem value="expense">Saídas</SelectItem>
          </SelectContent>
        </Select>

        {/* Filtros de conciliação apenas para contas conciliáveis */}
        {requiresReconciliation && (
          <>
            <Select value={sourceFilter} onValueChange={(v) => setSourceFilter(v as any)}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Origem" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="system">Sistema</SelectItem>
                <SelectItem value="bank">Banco</SelectItem>
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                <SelectItem value="reconciled">Conciliados</SelectItem>
                <SelectItem value="pending">Sem Conciliação</SelectItem>
                <SelectItem value="ignored">Ignorados</SelectItem>
              </SelectContent>
            </Select>
          </>
        )}
      </div>

      {/* Transactions Table */}
      <Card>
        <CardContent className="p-0">
          <ScrollArea className="h-[500px]">
            {isLoading ? (
              <div className="p-8 text-center text-muted-foreground">Carregando...</div>
            ) : filteredTransactions.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                Nenhuma movimentação encontrada
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-24">Data</TableHead>
                    {requiresReconciliation && <TableHead className="w-24">Origem</TableHead>}
                    <TableHead>Descrição</TableHead>
                    {requiresReconciliation && <TableHead className="w-32">Status</TableHead>}
                    <TableHead className="text-right w-32">Valor</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTransactions.map((transaction) => {
                    const { status, label, color } = getReconciliationStatus(transaction);
                    const isIncome = transaction.type === 'income';

                    return (
                      <TableRow key={`${transaction.source}-${transaction.id}`}>
                        <TableCell>
                          {format(toLocalDate(transaction.payment_date), 'dd/MM/yyyy', { locale: ptBR })}
                        </TableCell>
                        {requiresReconciliation && (
                          <TableCell>
                            {transaction.source === 'bank' && transaction.transfer_pair_id ? (
                              <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100 text-xs">
                                <ArrowLeftRight className="h-3 w-3 mr-1" />
                                Transferência
                              </Badge>
                            ) : transaction.source === 'bank' ? (
                              <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 text-xs">
                                <Landmark className="h-3 w-3 mr-1" />
                                Banco
                              </Badge>
                            ) : transaction.source === 'ledger' ? (
                              <Badge className={cn('text-xs', isIncome ? 'bg-green-100 text-green-800 hover:bg-green-100' : 'bg-red-100 text-red-800 hover:bg-red-100')}>
                                {isIncome ? <ArrowUpCircle className="h-3 w-3 mr-1" /> : <ArrowDownCircle className="h-3 w-3 mr-1" />}
                                {isIncome ? 'Receita' : 'Despesa'}
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="text-xs">
                                <Building2 className="h-3 w-3 mr-1" />
                                Sistema
                              </Badge>
                            )}
                          </TableCell>
                        )}
                        <TableCell className="max-w-[300px]">
                          <div className="flex flex-col">
                            <span className="font-medium truncate">
                              {/* Sem coluna Origem (conta não-conciliável), o badge da transferência vive junto à descrição (D-138) */}
                              {!requiresReconciliation && transaction.source === 'bank' && transaction.transfer_pair_id && (
                                <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100 text-xs mr-2 align-middle">
                                  <ArrowLeftRight className="h-3 w-3 mr-1" />
                                  Transferência
                                </Badge>
                              )}
                              {/* R-FIN.1: lançamento direto — mesmo padrão inline quando não há coluna Origem */}
                              {!requiresReconciliation && transaction.source === 'ledger' && (
                                <Badge className={cn('text-xs mr-2 align-middle', isIncome ? 'bg-green-100 text-green-800 hover:bg-green-100' : 'bg-red-100 text-red-800 hover:bg-red-100')}>
                                  {isIncome ? <ArrowUpCircle className="h-3 w-3 mr-1" /> : <ArrowDownCircle className="h-3 w-3 mr-1" />}
                                  {isIncome ? 'Receita' : 'Despesa'}
                                </Badge>
                              )}
                              {transaction.description}
                              {transaction.customer_or_supplier_name && (
                                <span className="text-muted-foreground font-normal"> - {transaction.customer_or_supplier_name}</span>
                              )}
                            </span>
                            {transaction.notes && (
                              <span className="text-xs text-muted-foreground truncate">{transaction.notes}</span>
                            )}
                          </div>
                        </TableCell>
                        {requiresReconciliation && (
                          <TableCell>
                            {status === 'ignored' && transaction.ignore_reason ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge className={`${color} hover:${color} text-xs gap-1`}>
                                    {getReconciliationIcon(status)}
                                    {label}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>{transaction.ignore_reason}</TooltipContent>
                              </Tooltip>
                            ) : (
                              <Badge className={`${color} hover:${color} text-xs gap-1`}>
                                {getReconciliationIcon(status)}
                                {label}
                              </Badge>
                            )}
                          </TableCell>
                        )}
                        <TableCell className="text-right">
                          <span className={`font-semibold ${isIncome ? 'text-green-600' : 'text-red-600'}`}>
                            {isIncome ? '+' : '-'} {formatCurrency(transaction.payment_value)}
                          </span>
                        </TableCell>
                        <TableCell className="p-1">
                          {/* Condição pelo dado (não pelo badge): o estorno existe também em conta não-conciliável (ex.: Cofre) */}
                          {transaction.source === 'bank' && transaction.transfer_pair_id && canReverse && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-7 w-7">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => setReverseTarget(transaction)}>
                                  <Undo2 className="h-4 w-4 mr-2" />
                                  Estornar transferência
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      <AlertDialog open={!!reverseTarget} onOpenChange={(open) => { if (!open) setReverseTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Estornar transferência?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                {reverseTarget && (
                  <p>
                    <span className="font-semibold">{formatCurrency(reverseTarget.payment_value)}</span>
                    {' em '}
                    {format(toLocalDate(reverseTarget.payment_date), 'dd/MM/yyyy', { locale: ptBR })}
                    {' — '}
                    {reverseTarget.description}
                  </p>
                )}
                <p>
                  As duas pernas da transferência serão desfeitas; se houver tarifa vinculada,
                  ela também será excluída.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={reverseTransfer.isPending}
              onClick={() => {
                if (reverseTarget) {
                  reverseTransfer.mutate(reverseTarget.id);
                  setReverseTarget(null);
                }
              }}
            >
              Estornar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
